/**
 * upnpStatePoller — Ground-truth UPnP renderer state + position polling.
 *
 * Polls AVTransport:GetTransportInfo + GetPositionInfo on the active UPnP
 * renderer. Corrects Zustand position from the real renderer position and
 * detects ghost playback (local says playing, renderer says stopped).
 *
 * When drift is detected and sustained (3 consecutive polls), triggers
 * auto-recovery via QueueController to re-send the current track.
 */

import { useMaestroStore } from '../store/maestroStore';
import { QueueController } from './QueueController';

const POLL_INTERVAL_MS = 3000;
const FETCH_TIMEOUT_MS = 2500;
const GHOST_THRESHOLD = 2; // consecutive drift polls before auto-recovery

let _pollTimer: ReturnType<typeof setInterval> | null = null;
let _activeZoneId: string | null = null;
let _consecutiveDriftCount = 0;
let _recovering = false;

const SOAP_GET_TRANSPORT_INFO = `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/"
  s:encodingStyle="http://schemas.xmlsoap.org/soap/encoding/">
  <s:Body>
    <u:GetTransportInfo xmlns:u="urn:schemas-upnp-org:service:AVTransport:1">
      <InstanceID>0</InstanceID>
    </u:GetTransportInfo>
  </s:Body>
</s:Envelope>`;

const SOAP_GET_POSITION_INFO = `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/"
  s:encodingStyle="http://schemas.xmlsoap.org/soap/encoding/">
  <s:Body>
    <u:GetPositionInfo xmlns:u="urn:schemas-upnp-org:service:AVTransport:1">
      <InstanceID>0</InstanceID>
    </u:GetPositionInfo>
  </s:Body>
</s:Envelope>`;

function extractXmlTag(xml: string, tag: string): string {
  try {
    const re = new RegExp(`<${tag}>([^<]*)</${tag}>`, 'i');
    const m = xml.match(re);
    return m ? m[1] : '';
  } catch {
    return '';
  }
}

function parseUpnpTime(timeStr: string): number {
  if (!timeStr || timeStr === 'NOT_IMPLEMENTED') return 0;
  const parts = timeStr.split(':');
  if (parts.length !== 3) return 0;
  const h = parseInt(parts[0]) || 0;
  const m = parseInt(parts[1]) || 0;
  const s = parseFloat(parts[2]) || 0;
  return h * 3600 + m * 60 + s;
}

function buildControlUrl(zone: any): string | null {
  const ip = zone?.ip || zone?.host;
  const port = zone?.port || 1400;
  if (!ip) return null;
  const avtPath = zone?.avt_url || zone?.avtUrl || '/MediaRenderer/AVTransport/Control';
  if (avtPath.startsWith('http')) return avtPath;
  return `http://${ip}:${port}${avtPath}`;
}

async function fetchWithTimeout(url: string, opts: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...opts, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function pollOnce(controlUrl: string, zoneName: string): Promise<void> {
  if (_recovering) return;

  const store = useMaestroStore.getState();
  const localState = store.playbackState;

  try {
    const [transportResp, positionResp] = await Promise.all([
      fetchWithTimeout(controlUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/xml; charset=utf-8',
          'SOAPAction': '"urn:schemas-upnp-org:service:AVTransport:1#GetTransportInfo"',
        },
        body: SOAP_GET_TRANSPORT_INFO,
      }, FETCH_TIMEOUT_MS),
      fetchWithTimeout(controlUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/xml; charset=utf-8',
          'SOAPAction': '"urn:schemas-upnp-org:service:AVTransport:1#GetPositionInfo"',
        },
        body: SOAP_GET_POSITION_INFO,
      }, FETCH_TIMEOUT_MS),
    ]);

    const transportXml = await transportResp.text();
    const positionXml = await positionResp.text();

    const remoteState = extractXmlTag(transportXml, 'CurrentTransportState');
    const relTime = extractXmlTag(positionXml, 'RelTime');
    const trackDuration = extractXmlTag(positionXml, 'TrackDuration');

    const remotePosSec = parseUpnpTime(relTime);
    const remoteDurSec = parseUpnpTime(trackDuration);

    // Correct position from renderer ground truth
    if (remoteState === 'PLAYING' && remotePosSec >= 0) {
      const localPos = store.position || 0;
      const drift = Math.abs(localPos - remotePosSec);

      // Correct if drifted more than 2 seconds
      if (drift > 2) {
        console.log(`[UPnPPoll] Correcting position: local=${Math.floor(localPos)}s → renderer=${Math.floor(remotePosSec)}s (drift=${Math.floor(drift)}s)`);
        store.updateTime(remotePosSec, remoteDurSec || store.duration);
      }

      _consecutiveDriftCount = 0;
    }

    // Detect ghost playback: we think we're playing but the renderer disagrees
    const stoppedStates = ['STOPPED', 'NO_MEDIA_PRESENT'];
    if (localState === 'playing' && stoppedStates.includes(remoteState)) {
      _consecutiveDriftCount++;
      console.warn(
        `[UPnPPoll] ⚠️ Ghost playback — local=playing, ${zoneName}=${remoteState} (${_consecutiveDriftCount}/${GHOST_THRESHOLD + 1})`,
      );

      if (_consecutiveDriftCount > GHOST_THRESHOLD) {
        console.error('[UPnPPoll] Ghost playback confirmed — attempting auto-recovery');
        _consecutiveDriftCount = 0;
        recoverGhostPlayback();
      }
    } else if (localState === 'playing' && remoteState === 'PLAYING') {
      _consecutiveDriftCount = 0;
    }

    // Detect track-ended: renderer stopped but we haven't advanced yet
    if (localState === 'playing' && remoteState === 'STOPPED' && remotePosSec === 0 && _consecutiveDriftCount <= 1) {
      // Could be natural track end — let QueueController's normal advance handle it
    }

  } catch (err: any) {
    const msg = String(err?.message || err || '');
    if (msg.includes('abort') || msg.includes('timeout')) {
      // Renderer unreachable — don't correct
    } else if (msg.includes('CORS') || msg.includes('NetworkError') || msg.includes('Failed to fetch') || msg.includes('Load failed')) {
      // Can't reach renderer from web context — expected on some setups
    } else {
      console.log('[UPnPPoll] poll error:', msg);
    }
  }
}

async function recoverGhostPlayback(): Promise<void> {
  _recovering = true;
  try {
    const store = useMaestroStore.getState();
    const currentTrack = store.currentTrack;
    if (!currentTrack) {
      store.setPlaybackState('stopped');
      return;
    }

    console.log('[UPnPPoll] Recovery: re-playing current track from beginning');
    store.setPlaybackState('loading');

    // Re-play current track via QueueController
    const queueIndex = store.queueIndex;
    await QueueController.playQueueTrack(queueIndex);
  } catch (err) {
    console.error('[UPnPPoll] Recovery failed:', err);
    useMaestroStore.getState().setPlaybackState('stopped');
  } finally {
    setTimeout(() => { _recovering = false; }, 5000);
  }
}

export function startUpnpStatePoll(zone: any): void {
  stopUpnpStatePoll();

  const controlUrl = buildControlUrl(zone);
  const zoneName = zone?.name || zone?.id || 'unknown';
  _activeZoneId = zone?.id || null;
  _consecutiveDriftCount = 0;

  if (!controlUrl) {
    console.log('[UPnPPoll] Cannot start — no IP/port for zone:', zoneName);
    return;
  }

  console.log('[UPnPPoll] Starting poll for', zoneName, 'at', controlUrl, '(every 3s)');

  pollOnce(controlUrl, zoneName);

  _pollTimer = setInterval(() => {
    const s = useMaestroStore.getState();
    if (s.playbackState !== 'playing' && s.playbackState !== 'loading') {
      stopUpnpStatePoll();
      return;
    }
    pollOnce(controlUrl, zoneName);
  }, POLL_INTERVAL_MS);
}

export function stopUpnpStatePoll(): void {
  if (_pollTimer) {
    clearInterval(_pollTimer);
    _pollTimer = null;
  }
  _activeZoneId = null;
  _consecutiveDriftCount = 0;
}

export function getActivePollerZoneId(): string | null {
  return _activeZoneId;
}
